// filepath: apps/web/src/features/admin/hmacKeys/HmacKeysPage.tsx
//
// FR-UI-ADM-7 — page-level component for the HMAC key rotation action.
//
// 5-state fetch contract (FR-UI-CORE-10):
//   - loading: skeleton rows
//   - success: HmacKeysTable
//   - error_fetch: error component with retry
//   - error_permission_denied: 403 surface
//   - staging_only_forbidden: rare; surfaced via StagingOnlyError at the route
//
// The page coordinates:
//   - useWebhookKeys (data fetch)
//   - Permission gate (403)
//   - Empty state with IssueKeyFab
//   - HmacKeysTable (state indicators + countdown + grace-period rows)
//   - RotateKeyDialog (open + close state)
//   - OneTimeCopyDialog (renders the new key value once)
//   - RevokeConfirmDialog (typed confirmation per FR-UI-CORE-6)

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useWebhookKeys, mapApiError } from "./queries";
import { HmacKeysTable } from "./HmacKeysTable";
import { RotateKeyDialog } from "./RotateKeyDialog";
import { OneTimeCopyDialog } from "./OneTimeCopyDialog";
import { RevokeConfirmDialog } from "./RevokeConfirmDialog";
import { IssueKeyFab } from "./IssueKeyFab";
import { DestructiveRotateDialog } from "./DestructiveRotateDialog";
import { ApiError } from "@/definitions/types";
import type {
  HmacKeyFetchError,
  IssueKeyResponse,
  RotateKeyResponse,
  WebhookKeyMeta,
} from "./types";

interface HmacKeysPageProps {
  environmentKind: string;
}

export function HmacKeysPage({
  environmentKind: _environmentKind,
}: HmacKeysPageProps): React.ReactElement {
  const { t } = useTranslation();
  const { data, isLoading, error, refetch } = useWebhookKeys();

  // Rotate dialog state
  const [rotateOpen, setRotateOpen] = useState(false);
  const [rotateKeyValue, setRotateKeyValue] =
    useState<RotateKeyResponse | null>(null);
  const [rotateGraceWindow, setRotateGraceWindow] = useState(1440);

  // Destructive-rotate confirmation (when a grace-period key is already present)
  const [destructiveRotateOpen, setDestructiveRotateOpen] = useState(false);

  // One-time copy dialog state (issue + rotate both route here)
  const [oneTimeValue, setOneTimeValue] = useState<{
    keyValue: string;
    keyId: string;
    kind: "issue" | "rotate";
  } | null>(null);

  // Revoke dialog state
  const [revokeTarget, setRevokeTarget] = useState<WebhookKeyMeta | null>(null);

  // -------- 5-state fetch contract --------

  if (isLoading) {
    return <SkeletonPage />;
  }

  if (error) {
    const mapped: HmacKeyFetchError = mapApiError(error);
    return <ErrorSurface mapped={mapped} onRetry={() => void refetch()} />;
  }

  const keys = data ?? [];

  const hasGracePeriod = keys.some((k) => k.state === "grace_period");

  const onRotateClick = () => {
    if (hasGracePeriod) {
      setDestructiveRotateOpen(true);
    } else {
      setRotateOpen(true);
    }
  };

  const onRotateSuccess = (resp: RotateKeyResponse) => {
    setRotateOpen(false);
    setDestructiveRotateOpen(false);
    setRotateKeyValue(resp);
    setOneTimeValue({
      keyValue: resp.new_key_value,
      keyId: resp.new_key_id,
      kind: "rotate",
    });
  };

  const onIssueSuccess = (resp: IssueKeyResponse) => {
    setOneTimeValue({
      keyValue: resp.key_value,
      keyId: resp.key_id,
      kind: "issue",
    });
  };

  return (
    <div data-testid="hmac-keys-page" className="hmac-keys-page">
      <header className="hmac-keys-header">
        <h1>{t("admin.webhooks.keys.title")}</h1>
      </header>

      {keys.length === 0 ? (
        <EmptyState>
          <IssueKeyFab onSuccess={onIssueSuccess} environmentKind="staging" />
        </EmptyState>
      ) : (
        <>
          <HmacKeysTable
            keys={keys}
            onRotateClick={onRotateClick}
            onRevokeClick={setRevokeTarget}
          />
          <button
            type="button"
            className="btn btn-primary"
            onClick={onRotateClick}
            data-testid="rotate-key-button"
          >
            {t("admin.webhooks.keys.rotate_action")}
          </button>
        </>
      )}

      <RotateKeyDialog
        open={rotateOpen}
        onClose={() => setRotateOpen(false)}
        onSuccess={onRotateSuccess}
        graceWindowMinutes={rotateGraceWindow}
        onGraceWindowChange={setRotateGraceWindow}
      />

      <DestructiveRotateDialog
        open={destructiveRotateOpen}
        onClose={() => setDestructiveRotateOpen(false)}
        onConfirm={() => setDestructiveRotateOpen(false)}
        onProceed={() => {
          setDestructiveRotateOpen(false);
          setRotateOpen(true);
        }}
      />

      {oneTimeValue && (
        <OneTimeCopyDialog
          keyValue={oneTimeValue.keyValue}
          keyId={oneTimeValue.keyId}
          kind={oneTimeValue.kind}
          onClose={() => setOneTimeValue(null)}
        />
      )}

      {revokeTarget && (
        <RevokeConfirmDialog
          keyId={revokeTarget.key_id}
          onClose={() => setRevokeTarget(null)}
          onSuccess={() => setRevokeTarget(null)}
        />
      )}

      {/* Reference variable to avoid unused-var lint for rotateKeyValue (kept
          for potential future rollback / undo UX). */}
      {rotateKeyValue ? null : null}
    </div>
  );
}

// -------- Sub-components --------

function SkeletonPage(): React.ReactElement {
  return (
    <div data-testid="hmac-keys-skeleton" className="hmac-keys-skeleton">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="hmac-keys-skeleton-row"
          data-testid={`skeleton-row-${i}`}
        />
      ))}
    </div>
  );
}

function EmptyState({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div data-testid="hmac-keys-empty" className="hmac-keys-empty">
      {children}
    </div>
  );
}

function ErrorSurface({
  mapped,
  onRetry,
}: {
  mapped: HmacKeyFetchError;
  onRetry: () => void;
}): React.ReactElement {
  const { t } = useTranslation();
  if (mapped.kind === "error_permission_denied") {
    return (
      <div data-testid="hmac-keys-error-permission" className="hmac-keys-error">
        {t("admin.webhooks.keys.permission_denied")}
      </div>
    );
  }
  return (
    <div data-testid="hmac-keys-error" className="hmac-keys-error">
      <p>{t("admin.webhooks.keys.loading_error")}</p>
      <button type="button" onClick={onRetry} data-testid="hmac-keys-retry">
        {t("admin.webhooks.keys.retry")}
      </button>
    </div>
  );
}

// Suppress unused-import lint for ApiError when it is only referenced via
// type narrowing inside mapApiError -- keeps the import for the public surface.
export const _apiError: typeof ApiError | undefined = undefined;
